import React from 'react';

import PromptBox from '../../PromptBox';
import { Button } from '../../ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import RemoveButton from '../components/RemoveButton';
import { IntakeFormData } from '../form/types';

interface MedicalSectionProps {
  form: IntakeFormData;
  onFieldChange: (
    section: keyof IntakeFormData,
    field: string,
    value: any,
  ) => void;
}

const MedicalSection: React.FC<MedicalSectionProps> = ({
  form,
  onFieldChange,
}) => {
  const handleInputChange = (field: string, value: any) => {
    onFieldChange('medicalWageHistory', field, value);
  };

  // Helper functions for treatment management
  const handleAddTreatment = () => {
    const newTreatments = [
      ...form.medicalWageHistory.treatments,
      {
        provider: '',
        dateOfService: '',
        treatmentReceived: '',
        stillTreating: false,
      },
    ];
    handleInputChange('treatments', newTreatments);
  };

  const handleUpdateTreatment = (index: number, field: string, value: any) => {
    const updatedTreatments = form.medicalWageHistory.treatments.map(
      (treatment, i) =>
        i === index ? { ...treatment, [field]: value } : treatment,
    );
    handleInputChange('treatments', updatedTreatments);
  };

  const handleRemoveTreatment = (index: number) => {
    const updatedTreatments = form.medicalWageHistory.treatments.filter(
      (_, i) => i !== index,
    );
    handleInputChange('treatments', updatedTreatments);
  };

  return (
    <div className="space-y-3">
      <PromptBox
        title="Suggested Questions"
        suggestions={[
          'What injuries did you notice immediately after the accident?',
          'What are your current symptoms and complaints?',
          'Have you sought any medical treatment since the incident?',
          'Are you experiencing any ongoing pain or symptoms?',
          'Did you receive ambulance or emergency medical care?',
        ]}
      />

      {/* Medical Treatment Information */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg
                className="w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
              <div>
                <CardTitle className="text-base">
                  Medical Treatment Information
                </CardTitle>
                <CardDescription className="text-xs text-gray-500 leading-tight">
                  Details about medical providers and treatments received
                </CardDescription>
              </div>
            </div>
            <Button
              type="button"
              onClick={handleAddTreatment}
              size="sm"
              tabIndex={0}
              className="text-xs px-3 py-1.5"
            >
              + Add Treatment
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {form.medicalWageHistory.treatments.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <svg
                className="w-10 h-10 mx-auto mb-3 text-muted-foreground/50"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="font-medium text-sm">No treatments recorded yet</p>
              <p className="text-xs text-muted-foreground">
                Click &quot;Add Treatment&quot; to begin documenting medical
                care
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {form.medicalWageHistory.treatments.map((treatment, index) => (
                <Card key={index} className="border-l-4 border-l-blue-500">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-medium">
                        Treatment #{index + 1}
                      </CardTitle>
                      <RemoveButton
                        onClick={() => handleRemoveTreatment(index)}
                        className=""
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label
                          htmlFor={`provider-${index}`}
                          className="text-sm font-medium"
                        >
                          Medical Provider
                        </Label>
                        <Input
                          id={`provider-${index}`}
                          value={treatment.provider}
                          onChange={(e) =>
                            handleUpdateTreatment(
                              index,
                              'provider',
                              e.target.value,
                            )
                          }
                          placeholder="Doctor, clinic, or hospital name"
                          aria-label="Medical provider"
                          tabIndex={0}
                          className="text-sm"
                        />
                      </div>

                      <div className="space-y-1">
                        <Label
                          htmlFor={`dateOfService-${index}`}
                          className="text-sm font-medium"
                        >
                          Date of Service
                        </Label>
                        <Input
                          id={`dateOfService-${index}`}
                          type="date"
                          value={treatment.dateOfService}
                          onChange={(e) =>
                            handleUpdateTreatment(
                              index,
                              'dateOfService',
                              e.target.value,
                            )
                          }
                          aria-label="Date of service"
                          tabIndex={0}
                          className="text-sm"
                        />
                      </div>
                    </div>

                    <div className="mt-3 space-y-1">
                      <Label
                        htmlFor={`treatmentReceived-${index}`}
                        className="text-sm font-medium"
                      >
                        Treatment Received
                      </Label>
                      <Textarea
                        id={`treatmentReceived-${index}`}
                        value={treatment.treatmentReceived}
                        onChange={(e) =>
                          handleUpdateTreatment(
                            index,
                            'treatmentReceived',
                            e.target.value,
                          )
                        }
                        placeholder="Describe the treatment, procedures, or care received..."
                        rows={3}
                        aria-label="Treatment received"
                        tabIndex={0}
                        className="text-sm"
                      />
                    </div>

                    <div className="mt-3 flex items-center space-x-2">
                      <Checkbox
                        id={`stillTreating-${index}`}
                        checked={treatment.stillTreating}
                        onCheckedChange={(checked) =>
                          handleUpdateTreatment(
                            index,
                            'stillTreating',
                            checked === true,
                          )
                        }
                        tabIndex={0}
                        className="border-gray-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white hover:border-gray-400 focus-visible:ring-blue-500/20"
                      />
                      <Label
                        htmlFor={`stillTreating-${index}`}
                        className="cursor-pointer text-sm text-gray-700"
                      >
                        Still treating with this provider
                      </Label>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employment Information */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Employment Information</CardTitle>
          <CardDescription className="text-xs text-gray-500 leading-tight">
            Work-related impacts and wage loss information
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="hasLostWages"
                checked={form.medicalWageHistory.hasLostWages}
                onCheckedChange={(checked) =>
                  handleInputChange('hasLostWages', checked)
                }
                tabIndex={0}
                className="border-gray-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white hover:border-gray-400 focus-visible:ring-blue-500/20"
              />
              <Label
                htmlFor="hasLostWages"
                className="cursor-pointer text-sm font-medium text-gray-700"
              >
                Lost Wages Due to Incident
              </Label>
            </div>

            {form.medicalWageHistory.hasLostWages && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="employer" className="text-sm font-medium">
                    Employer
                  </Label>
                  <Input
                    id="employer"
                    value={form.medicalWageHistory.employer}
                    onChange={(e) =>
                      handleInputChange('employer', e.target.value)
                    }
                    placeholder="Company or employer name"
                    aria-label="Employer"
                    tabIndex={0}
                    className="text-sm"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="jobTitle" className="text-sm font-medium">
                    Job Title
                  </Label>
                  <Input
                    id="jobTitle"
                    value={form.medicalWageHistory.jobTitle}
                    onChange={(e) =>
                      handleInputChange('jobTitle', e.target.value)
                    }
                    placeholder="Your job title or position"
                    aria-label="Job title"
                    tabIndex={0}
                    className="text-sm"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="hourlyWage" className="text-sm font-medium">
                    Hourly Wage
                  </Label>
                  <Input
                    id="hourlyWage"
                    value={form.medicalWageHistory.hourlyWage}
                    onChange={(e) =>
                      handleInputChange('hourlyWage', e.target.value)
                    }
                    placeholder="e.g., $15.00"
                    aria-label="Hourly wage"
                    tabIndex={0}
                    className="text-sm"
                  />
                </div>

                <div className="space-y-1">
                  <Label
                    htmlFor="employmentDuration"
                    className="text-sm font-medium"
                  >
                    Employment Duration
                  </Label>
                  <Input
                    id="employmentDuration"
                    value={form.medicalWageHistory.employmentDuration}
                    onChange={(e) =>
                      handleInputChange('employmentDuration', e.target.value)
                    }
                    placeholder="How long have you worked there?"
                    aria-label="Employment duration"
                    tabIndex={0}
                    className="text-sm"
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Medical History */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Medical History</CardTitle>
          <CardDescription className="text-xs text-gray-500 leading-tight">
            Prior medical conditions that may be relevant
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-1">
            <Label
              htmlFor="priorMedicalHistory"
              className="text-sm font-medium"
            >
              Prior Medical History
            </Label>
            <Textarea
              id="priorMedicalHistory"
              value={form.medicalWageHistory.priorMedicalHistory}
              onChange={(e) =>
                handleInputChange('priorMedicalHistory', e.target.value)
              }
              placeholder="Any pre-existing conditions, surgeries, or medical history that might be relevant..."
              rows={4}
              aria-label="Prior medical history"
              tabIndex={0}
              className="text-sm"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MedicalSection;
